import React from 'react'
import {
  Box,
  Paper,
  CardContent,
  Typography,
  Chip,
  useMediaQuery,
  Collapse,
  IconButton,
} from '@mui/material'
import { Theme } from '@mui/material/styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CloseIcon from '@mui/icons-material/Close'
import UndoIcon from '@mui/icons-material/Undo'
import RedoIcon from '@mui/icons-material/Redo'
import DownloadIcon from '@mui/icons-material/Download'

import useInventoryFiltersLogic from './InventoryFilters.logic'
import MultiSelectFilter from '../MultiSelectFilter/MultiSelectFilter'
import StyledButton from 'views/AssetsReports/components/StyledButton/StyledButton'
import SearchInput from 'components/molecules/SearchInput/SearchInput'

import styles from './InventoryFilters.module.scss'
import './InventorySearchInput.scss'
import {
  InventoryFiltersProps,
  FilterChipProps,
} from './InventoryFilters.types'

const InventoryFilters: React.FC<InventoryFiltersProps> = (props) => {
  const {
    filters,
    goBack,
    goForward,
    canGoBack,
    canGoForward,
    onReset,
    handleDownload,
    totalCount,
  } = props

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))

  const {
    expanded,
    activeFiltersCount,
    hasActiveFilters,
    vendorNames,
    locationNames,
    assetTypeNames,
    dispositionNames,
    productName,
    onHandFilterOptions,
    toggleExpanded,
    clearFilter,
    handleFilterChange,
    handleProductNameChange,
  } = useInventoryFiltersLogic(props)

  const FilterChip: React.FC<FilterChipProps> = ({ label, onDelete }) => {
    return (
      <Chip
        label={label}
        onDelete={onDelete}
        deleteIcon={<CloseIcon fontSize="small" />}
        size="small"
        className={`${styles.filterChip} ${isMobile ? styles.mobile : ''}`}
      />
    )
  }

  const renderFilterChips = () => {
    const chips: JSX.Element[] = []

    if (filters?.productName) {
      chips.push(
        <FilterChip
          key="productName"
          label={`Product: ${filters.productName}`}
          onDelete={() => clearFilter('productName')}
        />
      )
    }

    if (filters?.selectedCompanies && filters.selectedCompanies.length > 0) {
      filters.selectedCompanies.forEach((company: string, index: number) => {
        chips.push(
          <FilterChip
            key={`selectedCompanies-${index}`}
            label={`Company: ${company}`}
            onDelete={() => {
              const newCompanies = filters.selectedCompanies!.filter(
                (c: string) => c !== company
              )
              handleFilterChange(
                'selectedCompanies',
                newCompanies.length > 0 ? newCompanies : null
              )
            }}
          />
        )
      })
    }

    if (filters?.selectedLocations && filters.selectedLocations.length > 0) {
      filters.selectedLocations.forEach((location: string, index: number) => {
        chips.push(
          <FilterChip
            key={`selectedLocations-${index}`}
            label={`Location: ${location}`}
            onDelete={() => {
              const newLocations = filters.selectedLocations!.filter(
                (l: string) => l !== location
              )
              handleFilterChange(
                'selectedLocations',
                newLocations.length > 0 ? newLocations : null
              )
            }}
          />
        )
      })
    }

    if (filters?.type && filters.type.length > 0) {
      filters.type.forEach((type: string, index: number) => {
        chips.push(
          <FilterChip
            key={`type-${index}`}
            label={`Type: ${type}`}
            onDelete={() => {
              const newTypes =
                filters.type?.filter((t: string) => t !== type) || []
              handleFilterChange('type', newTypes.length > 0 ? newTypes : null)
            }}
          />
        )
      })
    }

    if (filters?.itemDisposition && filters.itemDisposition.length > 0) {
      filters.itemDisposition.forEach((disposition: string, index: number) => {
        chips.push(
          <FilterChip
            key={`itemDisposition-${index}`}
            label={`Disposition: ${disposition}`}
            onDelete={() => {
              const newDispositions =
                filters.itemDisposition?.filter(
                  (d: string) => d !== disposition
                ) || []
              handleFilterChange(
                'itemDisposition',
                newDispositions.length > 0 ? newDispositions : null
              )
            }}
          />
        )
      })
    }

    if (filters?.onHandFilter && filters.onHandFilter.length > 0) {
      filters.onHandFilter.forEach((filter: string, index: number) => {
        const filterOption = onHandFilterOptions.find(
          (option) => option.value === filter
        )
        chips.push(
          <FilterChip
            key={`onHandFilter-${index}`}
            label={`On Hand: ${filterOption?.label || filter}`}
            onDelete={() => {
              const newFilters =
                filters.onHandFilter?.filter((f: string) => f !== filter) || []
              handleFilterChange(
                'onHandFilter',
                newFilters.length > 0 ? newFilters : null
              )
            }}
          />
        )
      })
    }

    return chips
  }

  const chips = renderFilterChips()

  return (
    <Paper elevation={2} className={styles.filtersContainer}>
      <CardContent
        className={`${styles.cardContent} ${isMobile ? styles.mobile : ''} ${
          expanded ? styles.expanded : ''
        }`}
      >
        <Box
          className={`${styles.headerContainer} ${
            isMobile ? styles.mobile : ''
          }`}
        >
          <Box
            className={`${styles.titleContainer} ${
              isMobile ? styles.mobile : ''
            }`}
          >
            <Typography variant="subtitle1" className={styles.title}>
              Filters ({totalCount})
              {activeFiltersCount > 0 && (
                <Box component="span" className={styles.activeFiltersBadge}>
                  {activeFiltersCount}
                </Box>
              )}
            </Typography>
            <StyledButton
              label={expanded ? 'Hide' : 'Show'}
              onClick={toggleExpanded}
              size="small"
              variant="outlined"
              color="primary"
              className={styles.expandButton}
              startIcon={
                expanded ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )
              }
            />
          </Box>

          {hasActiveFilters && (
            <Box
              className={`${styles.chipsContainer} ${
                isMobile ? styles.mobile : styles.desktop
              }`}
            >
              {chips}
            </Box>
          )}

          <Box
            className={`${styles.buttonsArea} ${isMobile ? styles.mobile : ''}`}
          >
            <IconButton
              aria-label="Undo filter change"
              onClick={goBack}
              disabled={!canGoBack}
              size="small"
              color="primary"
            >
              <UndoIcon />
            </IconButton>
            <IconButton
              aria-label="Redo filter change"
              onClick={goForward}
              disabled={!canGoForward}
              size="small"
              color="primary"
            >
              <RedoIcon />
            </IconButton>

            <StyledButton
              label="Reset"
              onClick={onReset}
              size="small"
              variant="outlined"
              disabled={!hasActiveFilters}
              fullWidth={isMobile}
            />

            <IconButton
              aria-label="Export report"
              onClick={handleDownload}
              size="small"
              color="primary"
              className={styles.exportButton}
            >
              <DownloadIcon />
            </IconButton>
          </Box>
        </Box>
      </CardContent>
      <Collapse in={expanded} timeout={300}>
        <CardContent
          className={`${styles.expandedContent} ${
            isMobile ? styles.mobile : ''
          }`}
        >
          <Box
            className={`${styles.filtersGrid} ${isMobile ? styles.mobile : ''}`}
          >
            <Box
              className={`inventory-search-container ${
                isMobile ? 'mobile' : ''
              }`}
            >
              <SearchInput
                onSearch={handleProductNameChange}
                placeholder="Search Products"
                label="Search Product Name"
                value={productName}
              />
            </Box>

            <MultiSelectFilter
              label="Manufacturer"
              options={vendorNames}
              selectedValues={filters?.selectedCompanies || null}
              onChange={(values: string[] | null) =>
                handleFilterChange('selectedCompanies', values)
              }
              compact={isMobile}
              disabled={vendorNames.length === 0}
            />

            <MultiSelectFilter
              compact={isMobile}
              label="Location"
              options={locationNames}
              selectedValues={filters?.selectedLocations || null}
              onChange={(values: string[] | null) =>
                handleFilterChange('selectedLocations', values)
              }
              disabled={locationNames.length === 0}
            />

            <MultiSelectFilter
              compact={isMobile}
              label="Product Type"
              options={assetTypeNames}
              selectedValues={filters?.type || null}
              onChange={(values: string[] | null) =>
                handleFilterChange('type', values)
              }
              disabled={assetTypeNames.length === 0}
            />

            <MultiSelectFilter
              compact={isMobile}
              label="Ownership"
              options={dispositionNames}
              selectedValues={filters?.itemDisposition || null}
              onChange={(values: string[] | null) =>
                handleFilterChange('itemDisposition', values)
              }
              disabled={dispositionNames.length === 0}
            />

            <MultiSelectFilter
              compact={isMobile}
              label="On Hand"
              options={onHandFilterOptions.map((option) => option.value)}
              selectedValues={filters?.onHandFilter || null}
              onChange={(values: string[] | null) =>
                handleFilterChange('onHandFilter', values)
              }
              disabled={onHandFilterOptions.length === 0}
            />
          </Box>
        </CardContent>
      </Collapse>
    </Paper>
  )
}

export default InventoryFilters
